import axios, { CancelTokenSource } from 'axios'
import _ from 'lodash'
import { Feature, MapBrowserEvent } from 'ol'
import Collection from 'ol/Collection'
import { asArray } from 'ol/color'
import { pointerMove } from 'ol/events/condition'
import { buffer, extend } from 'ol/extent'
import { FeatureLike } from 'ol/Feature'
import { GeoJSON } from 'ol/format'
import Point from 'ol/geom/Point'
import Select from 'ol/interaction/Select'
import TileLayer from 'ol/layer/Tile'
import Vector from 'ol/layer/Vector'
import { fromLonLat, transformExtent } from 'ol/proj'
import { TileWMS } from 'ol/source'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style, Text } from 'ol/style'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Accordion, Segment } from 'semantic-ui-react'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { useQuery } from '../../common/util'
import {
  LAYER_MAPMARKER_NEIGHBOUR_COLOUR,
  LAYER_MAPMARKER_PRIMARY_COLOUR,
  LAYER_PLACE_NAMES_PATH,
  MAP_PROJECTION,
  OPEN_LAYERS_PLACENAMES_LAYER_NAME,
  OPEN_LAYERS_PLACENAMES_LAYER_URL,
  PLACENAME_SERVICES_EMAIL,
  PLACENAME_SERVICES_PHONE,
  PLACENAME_SERVICES_PHONE_INTERNATIONAL,
  PLACENAMES_MAP_CLICK_ENABLED,
} from '../../environment'
import { DEFAULT_LAYERS } from '../map/map.constants'
import { MapUtils } from '../map/map.utils'
import { MapConfigComponentProps } from '../map/MapComponent'
import MapMarkers, { MapMarker } from '../map/MapMarkers'
import VctFooter from '../vct-footer/VctFooter'
import PlacenameSearchOverlay from './PlacenameSearchOverlay'
import {
  getPlacenameData,
  getPlaces,
  hasResetPlaceMapView,
  PlacenameDataType,
  resetPlaceMapView,
  StyledLayerType,
} from './slice'

const PlacenameMapConfig = ({ map, zoomTo, setReturnLocation, onSingleClick }: MapConfigComponentProps) => {
  const dispatch = useAppDispatch()
  const primaryMarker = useAppSelector(getPlacenameData)
  let candidatePlaces = useAppSelector(getPlaces)
  let resetMapView = useAppSelector(hasResetPlaceMapView)
  let otherMarkers = candidatePlaces.filter(candidate => candidate.latitude && candidate.longitude && candidate.placeId !== primaryMarker?.placeId)
  let selectedMapLabel = ''
  const history = useHistory()
  const [styledLayerCollection, setStyledLayerCollection] = useState<StyledLayerType[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const onIconClick = (data: PlacenameDataType) => {
    let path = `${LAYER_PLACE_NAMES_PATH}/${data.placeId}`
    history.push(path)
  }
  const query = useQuery()
  const [featuresCollection] = useState(new Collection<Feature>())
  const [featureInfo, setFeatureInfo] = useState<Feature>()
  let source: CancelTokenSource

  const INITIAL_POS = fromLonLat([133.349051, -20.395616])
  const DEFAULT_ZOOM_LEVEL = 5.7

  useEffect(() => {
    // FIXME-Luke Placenames introduces its own layers in other effects so we need to handle this in a useEffect called
    //  after the render rather than as part of the render
    if (map) {
      map.setLayers(DEFAULT_LAYERS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cancelGeoServerRequest = () => {
    if (source) {
      source.cancel('CANCEL')
    }
  }

  function hasAltGeometry(data: PlacenameDataType) {
    return data.altGeometry && data.altGeometry.length > 0 && data.altGeometry[0]
  }

  useEffect(
    () => {
      if (primaryMarker) {
        if (query.get('zoomto') !== 'false') {
          if (primaryMarker.altGeometry && primaryMarker.altGeometry.length > 0 && primaryMarker.altGeometry[0]) {
            const vector_format = new GeoJSON()
            const initialGeom = vector_format.readGeometry(primaryMarker.altGeometry[0])
            let bounds = initialGeom.getExtent().slice(0)
            primaryMarker.altGeometry.slice(1).forEach(
              (altGeometry) => {
                let geometry = (new GeoJSON()).readGeometry(altGeometry)
                extend(bounds, geometry.getExtent())
              },
            )

            const latValue = (bounds[3] - bounds[1]) * .4
            const lonValue = (bounds[2] - bounds[0]) * .4
            const bufferValue = (latValue > lonValue ? latValue : lonValue)

            bounds = buffer(bounds, bufferValue)
            bounds = transformExtent(bounds, ('EPSG:4326'), ('EPSG:3857'))
            if (map) {
              map.getView().fit(bounds, { size: map.getSize() })
            }
          } else {
            zoomTo(primaryMarker)
          }
        }
        setReturnLocation(primaryMarker)
      }
    },
    [primaryMarker],
  )

  useEffect(
    () => {
      if (resetMapView) {
        if (map) {
          map.getView().animate({
            center: INITIAL_POS,
            zoom: DEFAULT_ZOOM_LEVEL,
          })
        }
        setReturnLocation({ longitude: 0, latitude: 0 })
      }
      return () => {
        dispatch(resetPlaceMapView(false))
      }
    },
    [resetMapView],
  )

  useEffect(() => {
    let currentPlaces = candidatePlaces.slice(0)
    if (primaryMarker && candidatePlaces) {
      const isFound = candidatePlaces.some(e => e.placeId === primaryMarker.placeId)
      if (!isFound) {
        currentPlaces.push(primaryMarker)
      }
      selectedMapLabel = primaryMarker.officialName + ' (' + primaryMarker.typeDesignation + ')'
    }

    if (currentPlaces && currentPlaces.length > 0) {
      let uniquePlacesById = [
        ...new Map(currentPlaces.map((item) => [item['placeId'], item])).values(),
      ]
      const designationTypes = new Set<string>()
      uniquePlacesById.forEach((place) => {
        if (place.typeDesignation)
          designationTypes.add(place.typeDesignation)
      })
      let olLayerAttrList: StyledLayerType[] = []
      Array.from(designationTypes).forEach(
        (designationType) => {
          let placesByType = uniquePlacesById.filter(data => data.typeDesignation === designationType && hasAltGeometry(data) && data.dataType !== 'Road')
          if (placesByType.length > 0) {
            const style = new Style({
              stroke: new Stroke({
                color: 'rgb(160, 32, 240, 1)',
                width: 1,
              }),
              fill: new Fill({
                color: 'rgba(160, 32, 240, 0.2)',
              }),
            })

            olLayerAttrList.push({
              'fillColor': 'rgba(160, 32, 240, 0.2)',
              'type': designationType,
              'style': style,
              'className': 'placenameLayer-' + designationType,
              'data': placesByType,
            })
          }

        },
      )

      let roadPlaces = uniquePlacesById.filter(data => data.dataType === 'Road' && hasAltGeometry(data))
      if (roadPlaces.length > 0) {
        const style = new Style({
          stroke: new Stroke({
            color: 'rgb(240,32,32,0.65)',
            width: 4,
          }),
          fill: new Fill({
            color: 'rgba(255,0,0,0.7)',
          }),
        })

        olLayerAttrList.push({
          'fillColor': 'rgba(255,0,0,0.7)',
          'type': 'Road',
          'style': style,
          'className': 'placenameLayer-Road',
          'data': roadPlaces,
        })
      }
      setStyledLayerCollection(olLayerAttrList)
    }
    return () => {
      setStyledLayerCollection([])
    }
  }, [primaryMarker, candidatePlaces])

  useEffect(() => {
    if (featureInfo && (featureInfo?.get('placeId') !== primaryMarker?.placeId && !candidatePlaces?.some(place => place?.placeId === featureInfo?.get('placeId')))) {
      const labelStyle = new Style({
        text: new Text({
          font: '12px Calibri,sans-serif',
          overflow: true,
          fill: new Fill({
            color: '#007aff',
          }),
          stroke: new Stroke({
            color: '#fff',
            width: 3,
          }),
          offsetY: -18,
          text: featureInfo.get('name'),
        }),
      })

      featureInfo.setStyle(labelStyle)
      featuresCollection.push(featureInfo)
      return () => {
        featuresCollection.clear()
      }
    } else {
      featuresCollection.clear()
    }
  }, [featureInfo])

  function renderStyledLayer() {
    if (!_.isEmpty(styledLayerCollection) && styledLayerCollection.length > 0 && map) {
      const styledLayerComponents = styledLayerCollection.map((styledLayerSet) => {
          return (
            <PlacenameSearchOverlay key={styledLayerSet.type}
                                    map={map}
                                    styledLayerSet={styledLayerSet}
                                    className={styledLayerSet.className}
            />
          )
        },
      )
      return (
        <div className={'mapLayerDetails'}>
          <Accordion>
            <Segment compact>
              <Accordion.Title
                active={activeIndex === 0}
                content={'Active Layer'}
                index={0}
                onClick={(e, titleProps) => {
                  const index = titleProps.index as number
                  setActiveIndex(activeIndex === index ? -1 : index)
                }}
              />
              <Accordion.Content active={activeIndex === 0} content={styledLayerComponents}/>
            </Segment>
          </Accordion>
        </div>
      )

    }
  }

  const selected = new Style({
    zIndex: 1,
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.1)',
    }),
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.4)',
      width: 3,
    }),
    text: new Text({
      font: '12px Calibri,sans-serif',
      overflow: true,
      fill: new Fill({
        color: '#007aff',
      }),
      stroke: new Stroke({
        color: '#fff',
        width: 3,
      }),
      offsetY: -40,
      text: '',
    }),

  })

  const selectStyle = (feature: FeatureLike) => {
    let color = feature.get('fillColor') || 'rgba(0, 0, 0, 0.1)'
    let colorArray = asArray(color)
    selected.getFill().setColor(colorArray)
    let strokeColor = colorArray.slice(0)
    strokeColor[3] = 1
    selected.getStroke().setColor(strokeColor)
    selected.getStroke().setWidth(5)
    if (!feature.get('hasLocation')) {
      selected.getText().setText(feature.get('name'))
    } else {
      selected.getText().setText('')
    }
    return selected
  }

  const selectSingleClick = new Select({
    style: selectStyle,
  })

  const selectPointerMove = new Select({
    condition: pointerMove,
    style: selectStyle,
  })

  const wmsSource = new TileWMS({
    url: OPEN_LAYERS_PLACENAMES_LAYER_URL,
    params: { 'LAYERS': OPEN_LAYERS_PLACENAMES_LAYER_NAME, 'TILED': true, 'srs': MAP_PROJECTION },
    serverType: 'geoserver',
    projection: MAP_PROJECTION,
    transition: 0,
    crossOrigin: 'anonymous',

  })


  const placenamesTileLayer = new TileLayer({
    className: 'placename-layer',
    source: wmsSource,
  })

  const handleSingleClick = async (evt: MapBrowserEvent<UIEvent>) => {
    const pixelsValue = placenamesTileLayer.getData(evt.pixel)
    if ((pixelsValue instanceof Uint8ClampedArray || pixelsValue instanceof Uint8Array || pixelsValue instanceof Float32Array) && pixelsValue[3]) {
      if (PLACENAMES_MAP_CLICK_ENABLED) {
        const placeObj: any = await onSingleClick(evt, wmsSource)
        if (placeObj) {
          let path = `${LAYER_PLACE_NAMES_PATH}/${placeObj.properties.PLACE_ID}`
          history.push(path)
        }
      }
    } else {
      map?.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        if (layer.get('name')?.startsWith('placenameLayer-')) {
          const placeId = feature.get('id')
          if (placeId) {
            let path = `${LAYER_PLACE_NAMES_PATH}/${placeId}`
            history.push(path)
            return true
          }
        }
      }, {
        'layerFilter': function (layer) {
          return layer.get('name')?.startsWith('placenameLayer-')
        },
      })
    }

  }

  const handlePointerMove = async (evt: MapBrowserEvent<UIEvent>) => {
    if (evt.dragging) {
      return
    }
    if (map) {
      const pixel = map.getEventPixel(evt.originalEvent)
      const hit = map.forEachLayerAtPixel(pixel, function (layer, rgba) {
        return rgba[3] >= 10
      }, {
        'layerFilter': function (layer) {
          return layer === placenamesTileLayer || layer.get('name')?.startsWith('placenameLayer-')
        },
      })

      map.getTargetElement().style.cursor = hit ? 'pointer' : ''

      const geoMarkerHit = map.forEachLayerAtPixel(pixel, function (layer, rgba) {

        return rgba[3] >= 10
      }, {
        'layerFilter': function (layer) {
          return layer === placenamesTileLayer
        },
      })

      cancelGeoServerRequest()

      if (geoMarkerHit) {
        const viewResolution = /** @type {number} */ (map?.getView().getResolution())
        if (wmsSource) {
          const url = wmsSource.getFeatureInfoUrl(
            evt.coordinate,
            viewResolution ? viewResolution : 2000,
            MAP_PROJECTION,
            {
              'INFO_FORMAT': 'application/json',
            },
          )

          if (url) {
            try {
              const CancelToken = axios.CancelToken
              source = CancelToken.source()
              const response = await axios.create().get(url, { cancelToken: source.token })
              const placeObj = response.data
              if (placeObj.features.length > 0) {
                const labelPos = new Point(fromLonLat(MapUtils.longLatToCoord(placeObj.features[0].properties.LONGITUDE, placeObj.features[0].properties.LATITUDE)))
                const feature = new Feature({
                  geometry: labelPos,
                  name: placeObj.features[0].properties.NAME + (placeObj.features[0].properties.TYPE_DESIGNATION ? ' (' + placeObj.features[0].properties.TYPE_DESIGNATION + ')' : ''),
                  placeId: placeObj.features[0].properties.PLACE_ID,
                })
                setFeatureInfo(feature)
              } else {
                setFeatureInfo(null!)
              }
            } catch (error: any) {
              if (error.message !== 'CANCEL') {
                throw error
              }
            }

          }
        }

      } else {
        setFeatureInfo(null!)
      }
    }
  }

  useEffect(
    () => {
      if (map) {
        const vectorLayer = new Vector({
          className: 'placenames-geo-marker-label',
          source: new VectorSource({
              features: featuresCollection,
            },
          ),
        })

        map.addLayer(vectorLayer)

        map.addLayer(placenamesTileLayer)
        map.addInteraction(selectSingleClick)
        map.addInteraction(selectPointerMove)
        map.on('pointermove', handlePointerMove)
        map.on('singleclick', handleSingleClick)
        return () => {
          map.un('singleclick', handleSingleClick)
          map.un('pointermove', handlePointerMove)
          map.removeInteraction(selectPointerMove)
          map.removeInteraction(selectSingleClick)
          map.removeLayer(placenamesTileLayer)
          map.getTargetElement().style.cursor = ''
        }
        return () => {
          map!.removeLayer(vectorLayer)
        }
      }
    },
    [map],
  )

  return (
    <React.Fragment>
      <MapMarkers map={map}>
        {otherMarkers.map((d) => {
          return (
            <MapMarker<PlacenameDataType>
              key={d.id}
              latitude={d.latitude}
              longitude={d.longitude}
              data={d}
              label={d.searchTerm}
              onIconClick={onIconClick}
              iconColor={LAYER_MAPMARKER_NEIGHBOUR_COLOUR}
              iconSize={'large'}/>
          )
        })}
      </MapMarkers>

      {primaryMarker && primaryMarker.latitude && primaryMarker.longitude && <MapMarkers map={map}>
          <MapMarker<PlacenameDataType>
              latitude={primaryMarker.latitude}
              longitude={primaryMarker.longitude}
              data={primaryMarker}
              label={primaryMarker.searchTerm}
              onIconClick={onIconClick}
              markerClassName={'primaryMarker'}
              iconColor={LAYER_MAPMARKER_PRIMARY_COLOUR}
              iconSize={'big'}/>
      </MapMarkers>
      }
      <VctFooter contactEmail={PLACENAME_SERVICES_EMAIL} contactPhone={PLACENAME_SERVICES_PHONE}
                 contactPhoneInternational={PLACENAME_SERVICES_PHONE_INTERNATIONAL}/>
      {map && renderStyledLayer()}
    </React.Fragment>
  )

}

export default PlacenameMapConfig